import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { SITE_FEATURES } from "../../lib/features";
import { Play, Loader2, UserPlus, Sparkles, Swords, BookOpen, Mail, Store, ShieldCheck, Flame, Clock, X } from "lucide-react";
import Reveal from "../Reveal";
import { GUIDES } from "../../data/guides";
import { useOnlinePlayers } from "../../lib/useOnlinePlayers";

function CountUp({ to, suffix = "" }) {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return undefined;
    let raf = 0;
    const run = () => { const t0 = performance.now(); const tick = (t) => { const k = Math.min(1, (t - t0) / 1400); setV(Math.round(to * (1 - Math.pow(1 - k, 3)))); if (k < 1) raf = requestAnimationFrame(tick); }; raf = requestAnimationFrame(tick); };
    const io = new IntersectionObserver((e) => { if (e[0].isIntersecting) { run(); io.disconnect(); } }, { threshold: 0.4 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [to]);
  return <b ref={ref}>{v}{suffix}</b>;
}

function Sparkline({ data }) {
  const w = 220, h = 54, pad = 4;
  const d = data.length > 1 ? data : [data[0] || 0, data[0] || 0];
  const max = Math.max(1, ...d), min = Math.min(0, ...d);
  const pts = d.map((v, i) => [pad + (i / (d.length - 1)) * (w - pad * 2), h - pad - ((v - min) / (max - min || 1)) * (h - pad * 2)]);
  const line = pts.map((p) => p.join(",")).join(" ");
  const area = `${pad},${h - pad} ${line} ${w - pad},${h - pad}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="live-spark" preserveAspectRatio="none">
      <defs><linearGradient id="lsg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#ff3b55" stopOpacity=".55" /><stop offset="1" stopColor="#ff3b55" stopOpacity="0" /></linearGradient></defs>
      <polygon points={area} fill="url(#lsg)" /><polyline points={line} fill="none" stroke="#ff6b7d" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="3.5" fill="#fff" />
    </svg>
  );
}

// HUD de jogadores online: número REAL vindo do servidor do jogo (sem bots).
export function LiveOnlineHud() {
  const { online, status, history } = useOnlinePlayers();
  const live = status === "live";
  return (
    <div className={`pkx-card live-hud ${live ? "" : "off"}`}>
      <div className="live-main">
        <span className="live-pill"><i />{live ? "AO VIVO" : status === "loading" ? "CONECTANDO" : "SEM SINAL"}</span>
        <div className="live-num">{live ? <CountUp to={online} /> : <b>—</b>}</div>
        <div className="live-label">Jogadores online agora</div>
        <p className="live-note">{live ? "Contagem em tempo real de quem está dentro do jogo neste momento." : "Não foi possível consultar o servidor do jogo agora."}</p>
      </div>
      <div className="live-chart"><Sparkline data={history.length ? history : [0]} /><span>últimos minutos</span></div>
    </div>
  );
}

export function HomeHero({ onPlay, loadingPlay, account }) {
  const logoRef = useRef(null);
  useEffect(() => {
    const el = logoRef.current; if (!el) return undefined;
    const mv = (e) => { const x = (e.clientX / window.innerWidth - 0.5) * 2, y = (e.clientY / window.innerHeight - 0.5) * 2; el.style.transform = `translate3d(${x * 14}px, ${y * 10}px, 0) rotateY(${x * 5}deg) rotateX(${-y * 4}deg)`; };
    window.addEventListener("mousemove", mv, { passive: true });
    return () => window.removeEventListener("mousemove", mv);
  }, []);

  return (
    <section className="hero">
      <div className="pkx-wrap">
        <div className="hero-inner mx-auto">
          <div className="pkx-kicker reveal in">Mundo Pokémon · idle · navegador</div>
          <div className="hero-logo-wrap" style={{ perspective: 900 }}>
            <img ref={logoRef} className="hero-logo" src="/images/logo-pkx.png" alt="PKX Idle" />
          </div>
          <p className="hero-sub">
            Entre em um <b>mundo vivo</b> onde seus Pokémon <em>caçam por você</em>, a economia é <b>feita pelos jogadores</b> e a aventura nunca para. <em>Venha jogar</em>: abre no navegador e em 1 minuto você já está caçando rumo ao topo do <b>ranking</b>.
          </p>
          <div className="hero-cta">
            <button onClick={onPlay} disabled={loadingPlay} className="pkx-btn primary" style={{ padding: "17px 38px", fontSize: 14 }}>
              {loadingPlay ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} className="fill-white" />}<span>Jogar agora</span>
            </button>
            {account
              ? <Link to="/panel" className="pkx-btn ghost" style={{ padding: "17px 34px", fontSize: 14 }}>Meu painel</Link>
              : <Link to="/register" className="pkx-btn ghost" style={{ padding: "17px 34px", fontSize: 14 }}><UserPlus size={16} /><span>Criar conta</span></Link>}
          </div>
          <LiveOnlineHud />
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  { icon: Clock, t: "Caça que não para", d: "Seus Pokémon caçam sozinhos, em qualquer hunt, e o progresso continua mesmo quando você sai. Volte e colete o resultado." },
  { icon: BookOpen, t: "Pokédex e Pokélog", d: "Registre cada espécie e cumpra metas de abates por Pokémon, com recompensas em itens, XP e pontos." },
  { flag: "legendPath", icon: Flame, t: "Caminho das Lendas", d: "A partir do nível 80, escolha Articuno, Zapdos ou Moltres e destrave uma constelação de bônus exclusiva." },
  { icon: Swords, t: "Trade, amigos e Correio", d: "Troque Pokémon e itens com amigos em tempo real, converse em privado e receba recompensas da equipe." },
  { icon: Store, t: "RMT Bazar", d: "Compre e venda personagens e Pokémon com segurança dentro da plataforma." },
  { icon: ShieldCheck, t: "Conta protegida", d: "Verificação em duas etapas com QR Code, e-mail verificado e chave de resgate para nunca perder o acesso." },
];

export function HomeFeatures() {
  return (
    <section className="pkx-section">
      <div className="pkx-wrap">
        <Reveal className="text-center"><span className="pkx-kicker">O que você encontra</span><h2 className="pkx-h2">Feito para <span className="pkx-grad-text">jogar e progredir</span></h2>
          <p className="pkx-lead mx-auto">Sistemas profundos, um mundo que evolui toda semana e ferramentas pensadas para quem gosta de ver o personagem crescer.</p></Reveal>
        <div className="feat-grid">
          {FEATURES.filter((f) => !f.flag || SITE_FEATURES[f.flag]).map((f, i) => (
            <Reveal key={f.t} delay={(i % 3) * 90} className="pkx-card feat" data-tilt>
              <div className="feat-ico"><f.icon size={26} /></div><h3>{f.t}</h3><p>{f.d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const BIRDS = [
  { id: "articuno", file: "Articuno_144", fw: 96, name: "Gelo Eterno", legend: "Articuno", c: "#38bdf8", ls: [["Redução de dano", "+20%"], ["Chance de captura", "+10%"], ["EXP", "+6%"]] },
  { id: "zapdos", file: "Zapdos_145", fw: 96, name: "Tempestade", legend: "Zapdos", c: "#facc15", ls: [["Chance de crítico", "+10%"], ["Bônus de drop", "+10%"], ["EXP", "+6%"]] },
  { id: "moltres", file: "Moltres_146", fw: 128, name: "Chama Viva", legend: "Moltres", c: "#ef4444", ls: [["Dano dos golpes", "+20%"], ["Cura das poções", "+20%"], ["EXP", "+6%"]] },
];

function GuideModal({ onClose }) {
  const g = GUIDES.find((x) => x.slug === "caminho-das-lendas");
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k); document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", k); document.body.style.overflow = ""; };
  }, [onClose]);
  return (
    <div className="gm-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="gm-box" role="dialog" aria-modal="true">
        <button className="gm-x" onClick={onClose} aria-label="Fechar"><X size={18} /></button>
        <div className="gm-body">
          <p className="gm-sum">{g.summary}</p>
          {g.steps && (
            <div className="gm-block">
              <h3>Passo a passo</h3>
              {g.steps.map((st, i) => (
                <div key={st.img} className="gm-step">
                  <div className="flex items-center gap-3 mb-2"><span className="step-n !w-8 !h-8 text-sm">{i + 1}</span><div><b className="block text-white">{st.t}</b><span className="text-[13px] text-[#c8b6bb]">{st.d}</span></div></div>
                  <img className="step-img" src={`/images/guides/${st.img}.jpg`} alt={st.t} loading="lazy" />
                </div>
              ))}
            </div>
          )}
          {g.blocks.map((b) => (
            <div key={b.h} className="gm-block">
              <h3>{b.h}</h3>
              {b.p && <p>{b.p}</p>}
              {b.ul && <ul>{b.ul.map((l) => <li key={l}>{l}</li>)}</ul>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Novidade: a 2ª geração (Johto). Os três iniciais evoluem em ciclo (1ª → 2ª → 3ª forma), com a arte do jogo
const JOHTO = [
  { id: "chikorita", name: "Chikorita", type: "Planta", c: "#4ade80", k: 3, st: [
    { id: "chikorita", name: "Chikorita", w: 19, h: 34, f: 5, req: "Inicial" },
    { id: "bayleef", name: "Bayleef", w: 24, h: 51, f: 3, req: "Lv. 25 · 1 Leaf Stone" },
    { id: "meganium", name: "Meganium", w: 39, h: 57, f: 9, req: "Lv. 50 · 2 Leaf Stone" }] },
  { id: "cyndaquil", name: "Cyndaquil", type: "Fogo", c: "#fb923c", k: 4, st: [
    { id: "cyndaquil", name: "Cyndaquil", w: 19, h: 25, f: 5, req: "Inicial" },
    { id: "quilava", name: "Quilava", w: 22, h: 28, f: 5, req: "Lv. 20 · 1 Fire Stone" },
    { id: "typhlosion", name: "Typhlosion", w: 33, h: 46, f: 3, req: "Lv. 60 · 2 Fire Stone" }] },
  { id: "totodile", name: "Totodile", type: "Água", c: "#38bdf8", k: 3, st: [
    { id: "totodile", name: "Totodile", w: 29, h: 31, f: 3, req: "Inicial" },
    { id: "croconaw", name: "Croconaw", w: 32, h: 41, f: 3, req: "Lv. 30 · 1 Water Stone" },
    { id: "feraligatr", name: "Feraligatr", w: 47, h: 52, f: 3, req: "Lv. 50 · 2 Water Stone" }] },
];
const JOHTO_CHIPS = ["94 novos Pokémon", "Versões shiny", "Golpes e efeitos novos", "Tipo Sombrio", "Evolução por pedras"];

export function HomeJohto() {
  return (
    <section className="pkx-section">
      <div className="pkx-wrap">
        <Reveal className="text-center"><span className="pkx-kicker">Novidade</span><h2 className="pkx-h2">A segunda geração <span className="pkx-grad-text">chegou</span></h2>
          <p className="pkx-lead mx-auto">Johto abre as portas do PKX Idle. Chikorita, Cyndaquil e Totodile encabeçam 94 novos Pokémon para caçar, capturar e evoluir, cada um com seus golpes, efeitos e uma versão shiny para os mais sortudos. Acompanhe os iniciais crescendo, da primeira forma até a final, e monte o time que vai dominar a nova região.</p></Reveal>
        <div className="birds-show">
          {JOHTO.map((l, i) => (
            <Reveal key={l.id} delay={i * 120} className="pkx-card bird-card jt-card" style={{ "--c": l.c }} data-tilt>
              <div className="jt-stage">
                {l.st.map((s, j) => (
                  <i key={s.id} className="jt-sprite" role="img" aria-label={s.name}
                    style={{ "--img": `url(/Pokemon/johto/${s.id}_front.png)`, "--w": `${s.w * l.k}px`, "--h": `${s.h * l.k}px`, "--n": s.f, "--d": `${(s.f * 0.14).toFixed(2)}s`, "--delay": `${j * 3.4}s` }} />
                ))}
              </div>
              <div className="jt-names">{l.st.map((s, j) => <h3 key={s.id} style={{ "--delay": `${j * 3.4}s` }}>{s.name}</h3>)}</div>
              <small>{l.type} · Inicial de Johto</small>
              <ul>{l.st.map((s) => <li key={s.id}><span>{s.name}</span><b>{s.req}</b></li>)}</ul>
            </Reveal>
          ))}
        </div>
        <Reveal className="jt-chips">{JOHTO_CHIPS.map((c) => <span key={c}>{c}</span>)}</Reveal>
        <p className="text-center text-[13px] text-[#b9a2a8] mt-4">Os lendários de Johto chegam numa próxima atualização.</p>
      </div>
    </section>
  );
}

export function HomeBirds() {
  const [open, setOpen] = useState(false);
  if (!SITE_FEATURES.legendPath) return null;   // Caminho das Lendas desligado (lib/features.js): a seção some da página inicial
  return (
    <section className="pkx-section">
      <div className="pkx-wrap">
        <Reveal className="text-center"><span className="pkx-kicker">Novo sistema</span><h2 className="pkx-h2">Caminho das <span className="pkx-grad-text">Lendas</span></h2>
          <p className="pkx-lead mx-auto">No nível 80, cada treinador escolhe uma ave lendária e destrava uma constelação de bônus. Um caminho por personagem, com os bônus sempre ativos e sem mexer nos IVs.</p></Reveal>
        <div className="birds-show">
          {BIRDS.map((b, i) => (
            <Reveal key={b.id} delay={i * 120} className="pkx-card bird-card" style={{ "--c": b.c }} data-tilt>
              <i className="bird-anim" role="img" aria-label={b.legend} style={{ "--sheet": `url(/Pokemon/${b.file}.png)`, "--fw": `${b.fw}px`, "--sz": "192px" }} />
              <h3>{b.name}</h3><small>{b.legend}</small>
              <ul>{b.ls.map(([k, v]) => <li key={k}><span>{k}</span><b>{v}</b></li>)}</ul>
            </Reveal>
          ))}
        </div>
        <Reveal className="text-center mt-8"><button onClick={() => setOpen(true)} className="pkx-btn ghost"><Sparkles size={15} /><span>Ver como funciona</span></button></Reveal>
      </div>
      {open && createPortal(<GuideModal onClose={() => setOpen(false)} />, document.body)}
    </section>
  );
}

export function NewsHeader() {
  return (
    <Reveal className="text-center pt-10"><span className="pkx-kicker">Fique por dentro</span><h2 className="pkx-h2">Novidades do <span className="pkx-grad-text">servidor</span></h2></Reveal>
  );
}
